import type { TableColumnsType } from 'antd';
import {
  Alert,
  Button,
  Descriptions,
  Drawer,
  message,
  Select,
  Space,
  Table,
  Tag,
  Tooltip,
} from 'antd';
import { Download, RefreshCcw, ScrollText } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  tuttiComputerApi,
  tuttiComputerErrorMessage,
} from '@/services/tuttiComputerApi';
import type {
  ComputerDiagnosticTarget,
  ComputerRuntimeLogRangePreset,
  ComputerRuntimeLogRecall,
  TuttiComputer,
} from '@/types';
import { ComputerExecutionDiagnosticsSection } from './ComputerExecutionDiagnostics';

const RANGE_OPTIONS: Array<{
  label: string;
  value: Exclude<ComputerRuntimeLogRangePreset, 'unknown'>;
}> = [
  { label: '最近 1 小时', value: '1_hour' },
  { label: '最近 3 小时', value: '3_hours' },
  { label: '最近 1 天', value: '1_day' },
  { label: '最近 3 天', value: '3_days' },
  { label: '最近 14 天', value: '14_days' },
];

const RANGE_LABELS: Record<ComputerRuntimeLogRangePreset, string> = {
  '1_hour': '最近 1 小时',
  '3_hours': '最近 3 小时',
  '1_day': '最近 1 天',
  '3_days': '最近 3 天',
  '14_days': '最近 14 天',
  unknown: '未知范围',
};

const ACTIVE_TASK_POLL_INTERVAL_MS = 30_000;

function formatDateTime(value: string) {
  if (!value) {
    return '-';
  }
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? value
    : date.toLocaleString('zh-CN', { hour12: false });
}

function formatBytes(value: string) {
  const bytes = Number(value);
  if (!Number.isFinite(bytes) || bytes < 0) {
    return '-';
  }
  if (bytes < 1024) {
    return `${bytes} B`;
  }
  const units = ['KiB', 'MiB', 'GiB'];
  let size = bytes / 1024;
  let unitIndex = 0;
  while (size >= 1024 && unitIndex < units.length - 1) {
    size /= 1024;
    unitIndex += 1;
  }
  return `${size.toFixed(size >= 10 ? 1 : 2)} ${units[unitIndex]}`;
}

function formatDuration(value: string) {
  const milliseconds = Number(value);
  if (!Number.isFinite(milliseconds) || milliseconds < 0) {
    return '-';
  }
  return milliseconds < 1000
    ? `${milliseconds} ms`
    : `${(milliseconds / 1000).toFixed(1)} s`;
}

function platformLabel(platform: string) {
  switch (platform.toLowerCase()) {
    case 'darwin':
      return 'macOS';
    case 'windows':
      return 'Windows';
    case 'linux':
      return 'Linux';
    default:
      return platform || '-';
  }
}

function ConnectionTag({ computer }: { computer: TuttiComputer }) {
  if (computer.connectionStatus === 'online') {
    return <Tag color="success">在线</Tag>;
  }
  if (computer.connectionStatus === 'offline') {
    return <Tag>离线</Tag>;
  }
  return <Tag color="warning">未知</Tag>;
}

function RecallStateTag({ task }: { task: ComputerRuntimeLogRecall }) {
  switch (task.state) {
    case 'waiting_for_device':
      return (
        <Tag color="blue">
          {task.collectionSource === 'cloud' ? '等待采集' : '等待设备接收'}
        </Tag>
      );
    case 'device_processing':
      return (
        <Tag color="processing">
          {task.collectionSource === 'cloud' ? '采集中' : '设备处理中'}
        </Tag>
      );
    case 'downloadable':
      return <Tag color="success">可下载</Tag>;
    case 'failed':
      return <Tag color="error">失败</Tag>;
    default:
      return <Tag color="warning">未知状态</Tag>;
  }
}

function isActiveTask(task: ComputerRuntimeLogRecall) {
  return (
    task.state === 'waiting_for_device' || task.state === 'device_processing'
  );
}

interface TuttiComputerDrawerProps {
  computerId: string;
  initialComputer: TuttiComputer | null;
  diagnosticTarget: ComputerDiagnosticTarget | null;
  open: boolean;
  onClose: () => void;
}

export function TuttiComputerDrawer({
  computerId,
  initialComputer,
  diagnosticTarget,
  open,
  onClose,
}: TuttiComputerDrawerProps) {
  const currentComputerIdRef = useRef(computerId);
  currentComputerIdRef.current = computerId;
  const [computer, setComputer] = useState<TuttiComputer | null>(
    initialComputer,
  );
  const [detailLoading, setDetailLoading] = useState(false);
  const [tasks, setTasks] = useState<ComputerRuntimeLogRecall[]>([]);
  const [tasksLoading, setTasksLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [nextPageToken, setNextPageToken] = useState('');
  const [rangePreset, setRangePreset] =
    useState<Exclude<ComputerRuntimeLogRangePreset, 'unknown'>>('1_hour');
  const [sessionId, setSessionId] = useState('');
  const [turnId, setTurnId] = useState('');
  const [creating, setCreating] = useState(false);
  const [downloadingTaskId, setDownloadingTaskId] = useState('');

  const loadComputer = useCallback(
    async (showLoading: boolean, showError: boolean) => {
      if (!open || !computerId) {
        return;
      }
      if (showLoading) {
        setDetailLoading(true);
      }
      try {
        const result = await tuttiComputerApi.get(computerId);
        if (currentComputerIdRef.current === computerId) {
          setComputer(result);
        }
      } catch (error) {
        if (showError) {
          message.error(
            tuttiComputerErrorMessage(error, '加载 Computer 详情失败'),
          );
        }
      } finally {
        if (showLoading && currentComputerIdRef.current === computerId) {
          setDetailLoading(false);
        }
      }
    },
    [computerId, open],
  );

  const loadFirstTaskPage = useCallback(
    async (showError: boolean) => {
      if (!open || !computerId) {
        return;
      }
      setTasksLoading(true);
      try {
        const result = await tuttiComputerApi.listRuntimeLogRecalls(computerId);
        if (currentComputerIdRef.current === computerId) {
          setTasks(result.tasks);
          setNextPageToken(result.nextPageToken);
        }
      } catch (error) {
        if (showError) {
          message.error(
            tuttiComputerErrorMessage(error, '加载日志召回记录失败'),
          );
        }
      } finally {
        if (currentComputerIdRef.current === computerId) {
          setTasksLoading(false);
        }
      }
    },
    [computerId, open],
  );

  useEffect(() => {
    if (!open || !computerId) {
      return;
    }
    setComputer(initialComputer);
    setTasks([]);
    setNextPageToken('');
    setRangePreset('1_hour');
    setSessionId('');
    setTurnId('');
    setLoadingMore(false);
    setCreating(false);
    setDownloadingTaskId('');
    void loadComputer(true, true);
    void loadFirstTaskPage(true);
  }, [computerId, initialComputer, loadComputer, loadFirstTaskPage, open]);

  useEffect(() => {
    if (!open || !computerId) {
      return;
    }
    const timer = window.setInterval(() => {
      void loadComputer(false, false);
    }, 30_000);
    return () => window.clearInterval(timer);
  }, [computerId, loadComputer, open]);

  const activeTaskIds = useMemo(
    () => tasks.filter(isActiveTask).map((task) => task.taskId),
    [tasks],
  );
  const activeTaskKey = activeTaskIds.join(',');

  useEffect(() => {
    if (!open || !computerId || !activeTaskKey) {
      return;
    }
    let polling = false;
    const timer = window.setInterval(async () => {
      if (polling) {
        return;
      }
      polling = true;
      try {
        const updates = await Promise.all(
          activeTaskIds.map((taskId) =>
            tuttiComputerApi
              .getRuntimeLogRecall(computerId, taskId)
              .catch(() => null),
          ),
        );
        if (currentComputerIdRef.current !== computerId) {
          return;
        }
        const byId = new Map(
          updates
            .filter((task): task is ComputerRuntimeLogRecall => task !== null)
            .map((task) => [task.taskId, task]),
        );
        setTasks((current) =>
          current.map((task) => byId.get(task.taskId) ?? task),
        );
      } finally {
        polling = false;
      }
    }, ACTIVE_TASK_POLL_INTERVAL_MS);
    return () => window.clearInterval(timer);
  }, [activeTaskIds, activeTaskKey, computerId, open]);

  const handleCreateRecall = async () => {
    if (!computer || !computer.canCollect) {
      message.warning(computer?.collectionBlockedReason || '当前不能发起采集');
      return;
    }
    if (tasks.some(isActiveTask)) {
      message.warning('该设备已有进行中的日志召回任务');
      return;
    }
    const targetComputerId = computer.computerId;
    setCreating(true);
    try {
      const created = await tuttiComputerApi.createRuntimeLogRecall(
        targetComputerId,
        rangePreset,
        {
          sessionId: sessionId.trim() || undefined,
          turnId: turnId.trim() || undefined,
        },
      );
      if (currentComputerIdRef.current !== targetComputerId) {
        return;
      }
      setTasks((current) => [
        created,
        ...current.filter((task) => task.taskId !== created.taskId),
      ]);
      if (created.state === 'failed') {
        message.error(created.failureMessage || '日志召回指令下发失败');
        return;
      }
      message.success(
        computer.computerKind === 'cloud'
          ? '云端诊断采集任务已创建'
          : '日志召回任务已创建，等待设备接收',
      );
    } catch (error) {
      if (currentComputerIdRef.current === targetComputerId) {
        message.error(tuttiComputerErrorMessage(error, '日志召回下发失败'));
      }
    } finally {
      if (currentComputerIdRef.current === targetComputerId) {
        setCreating(false);
      }
    }
  };

  const handleLoadMore = async () => {
    if (!computerId || !nextPageToken || loadingMore) {
      return;
    }
    setLoadingMore(true);
    try {
      const result = await tuttiComputerApi.listRuntimeLogRecalls(
        computerId,
        nextPageToken,
      );
      if (currentComputerIdRef.current !== computerId) {
        return;
      }
      setTasks((current) => {
        const knownIds = new Set(current.map((task) => task.taskId));
        return [
          ...current,
          ...result.tasks.filter((task) => !knownIds.has(task.taskId)),
        ];
      });
      setNextPageToken(result.nextPageToken);
    } catch (error) {
      message.error(
        tuttiComputerErrorMessage(error, '加载更多日志召回记录失败'),
      );
    } finally {
      if (currentComputerIdRef.current === computerId) {
        setLoadingMore(false);
      }
    }
  };

  const handleDownload = async (task: ComputerRuntimeLogRecall) => {
    setDownloadingTaskId(task.taskId);
    try {
      const download = await tuttiComputerApi.createRuntimeLogRecallDownload(
        task.computerId,
        task.taskId,
      );
      if (!download.downloadUrl) {
        throw new Error('服务未返回下载地址');
      }
      const link = document.createElement('a');
      link.href = download.downloadUrl;
      link.download = download.fileName || `${task.taskId}.tar.gz`;
      link.target = '_blank';
      link.rel = 'noopener noreferrer';
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      message.error(tuttiComputerErrorMessage(error, '生成下载链接失败'));
    } finally {
      setDownloadingTaskId('');
    }
  };

  const columns: TableColumnsType<ComputerRuntimeLogRecall> = [
    {
      title: '状态',
      dataIndex: 'state',
      width: 150,
      render: (_value, task) => <RecallStateTag task={task} />,
    },
    {
      title: '召回范围',
      dataIndex: 'rangePreset',
      width: 115,
      render: (value: ComputerRuntimeLogRangePreset) => RANGE_LABELS[value],
    },
    {
      title: '发起时间',
      dataIndex: 'createdAt',
      width: 175,
      render: (value: string, task) => (
        <div>
          <div>{formatDateTime(value)}</div>
          <Tooltip title={task.taskId}>
            <div className="max-w-40 truncate font-mono text-xs text-muted-foreground">
              {task.taskId}
            </div>
          </Tooltip>
        </div>
      ),
    },
    {
      title: '结果',
      key: 'result',
      width: 220,
      render: (_value, task) => {
        if (task.state === 'failed') {
          return (
            <Tooltip title={task.failureMessage || task.failureCode}>
              <div className="max-w-52 truncate text-red-600">
                {task.failureMessage || task.failureCode || '处理失败'}
              </div>
            </Tooltip>
          );
        }
        if (task.state === 'downloadable') {
          return (
            <Space direction="vertical" size={0}>
              <span>{formatBytes(task.compressedBytes)}</span>
              <span className="text-xs text-muted-foreground">
                采集 {formatDuration(task.collectionDurationMs)} · 上传{' '}
                {formatDuration(task.uploadDurationMs)}
              </span>
              {task.truncated ? (
                <span className="text-xs text-amber-600">
                  已按 300 MiB 上限截断
                </span>
              ) : null}
            </Space>
          );
        }
        return <span className="text-muted-foreground">-</span>;
      },
    },
    {
      title: '操作',
      key: 'actions',
      fixed: 'right',
      width: 100,
      render: (_value, task) => {
        if (task.state !== 'downloadable') {
          return null;
        }
        if (task.downloadExpired) {
          return <span className="text-xs text-muted-foreground">已过期</span>;
        }
        return (
          <Button
            type="link"
            className="px-0"
            icon={<Download className="h-4 w-4" />}
            loading={downloadingTaskId === task.taskId}
            onClick={() => void handleDownload(task)}
          >
            下载
          </Button>
        );
      },
    },
  ];

  const canCollect = computer?.canCollect === true;
  const cloud = computer?.computerKind === 'cloud';
  const hasActiveTask = tasks.some(isActiveTask);

  return (
    <Drawer
      title="Tutti Computer 详情"
      open={open}
      onClose={onClose}
      width={860}
      destroyOnHidden
    >
      {computer ? (
        <div className="space-y-6">
          {diagnosticTarget ? (
            <Alert
              type="info"
              showIcon
              title={`Target ${diagnosticTarget.targetId}`}
              description={`Provider: ${diagnosticTarget.providerType}`}
            />
          ) : null}
          <Descriptions
            bordered
            size="small"
            column={{ xs: 1, sm: 2 }}
            items={[
              {
                key: 'name',
                label: '设备名称',
                children:
                  computer.displayName || computer.reportedName || '未命名',
              },
              {
                key: 'status',
                label: '连接状态',
                children: <ConnectionTag computer={computer} />,
              },
              {
                key: 'id',
                label: 'Computer ID',
                children: (
                  <span className="break-all font-mono text-xs">
                    {computer.computerId}
                  </span>
                ),
              },
              {
                key: 'owner',
                label: '所属用户',
                children: (
                  <div>
                    <div>
                      {computer.owner.name || computer.owner.email || '-'}
                    </div>
                    {computer.owner.name && computer.owner.email ? (
                      <div className="text-xs text-muted-foreground">
                        {computer.owner.email}
                      </div>
                    ) : null}
                  </div>
                ),
              },
              {
                key: 'platform',
                label: '系统',
                children:
                  [platformLabel(computer.platform), computer.arch]
                    .filter(Boolean)
                    .join(' / ') || '-',
              },
              {
                key: 'computer-version',
                label: 'Computer 版本',
                children: computer.computerVersion || '-',
              },
              {
                key: 'lastSeen',
                label: '最后在线',
                children: formatDateTime(computer.lastSeenAt),
              },
              {
                key: 'binding',
                label: '绑定版本',
                children: computer.bindingRevision || '-',
              },
            ]}
          />

          {computer.cloudEnvironment ? (
            <section className="space-y-3">
              <h2 className="font-semibold">Cloud 环境</h2>
              <Descriptions
                bordered
                size="small"
                column={2}
                items={[
                  {
                    key: 'id',
                    label: '环境 ID',
                    children: computer.cloudEnvironment.environmentId,
                  },
                  {
                    key: 'state',
                    label: '环境状态',
                    children: computer.cloudEnvironment.state,
                  },
                  {
                    key: 'program',
                    label: '程序版本',
                    children: (
                      <span className="break-all font-mono text-xs">
                        {computer.cloudEnvironment.programReleaseId || '-'}
                      </span>
                    ),
                  },
                  {
                    key: 'generation',
                    label: 'Worker Generation',
                    children: computer.cloudEnvironment.workerGeneration || '-',
                  },
                ]}
              />
              <Table
                size="small"
                rowKey="providerType"
                pagination={false}
                dataSource={computer.cloudEnvironment.providers}
                locale={{ emptyText: '尚无 Provider 检测结果' }}
                columns={[
                  { title: 'Provider', dataIndex: 'providerType' },
                  { title: 'Readiness', dataIndex: 'state' },
                  { title: '详情', dataIndex: 'detail' },
                ]}
              />
            </section>
          ) : null}

          <ComputerExecutionDiagnosticsSection
            key={computer.computerId}
            computerId={computer.computerId}
            sessionId={sessionId}
            turnId={turnId}
            onSessionIdChange={setSessionId}
            onTurnIdChange={setTurnId}
          />

          <section className="rounded-lg border border-border bg-slate-50 p-4">
            <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
              <div>
                <div className="flex items-center gap-2">
                  <ScrollText className="h-4 w-4 text-accent" />
                  <h2 className="font-semibold">
                    {cloud ? '采集云端诊断' : '召回 desktopd 日志'}
                  </h2>
                </div>
                <p className="mt-1 text-sm text-muted-foreground">
                  {cloud
                    ? '服务端采集环境、服务与执行日志。暂停实例在采集时恢复，worker 离线不影响发起采集。'
                    : '指令下发后由该设备筛选、打包并上传日志，单包上限 300 MiB。'}
                </p>
              </div>
              <Space wrap>
                <Select
                  aria-label="日志时间范围"
                  value={rangePreset}
                  options={RANGE_OPTIONS}
                  className="w-36"
                  disabled={!canCollect || creating || hasActiveTask}
                  onChange={setRangePreset}
                />
                <Tooltip
                  title={
                    !canCollect
                      ? computer.collectionBlockedReason || '当前不能发起采集'
                      : hasActiveTask
                        ? '该设备已有进行中的任务'
                        : undefined
                  }
                >
                  <span>
                    <Button
                      type="primary"
                      disabled={!canCollect || hasActiveTask}
                      loading={creating}
                      onClick={() => void handleCreateRecall()}
                    >
                      {creating
                        ? '创建中'
                        : cloud
                          ? '采集云端诊断'
                          : '发起日志召回'}
                    </Button>
                  </span>
                </Tooltip>
              </Space>
            </div>
            {!canCollect ? (
              <Alert
                className="mt-4"
                type="warning"
                showIcon
                title="当前不能发起采集"
                description={
                  computer.collectionBlockedReason ||
                  '请刷新 Computer 详情查看采集条件。'
                }
              />
            ) : null}
          </section>

          <section>
            <div className="mb-3 flex items-center justify-between gap-3">
              <div>
                <h2 className="font-semibold">日志召回记录</h2>
                <p className="text-sm text-muted-foreground">
                  进行中的任务每 30 秒更新一次状态。
                </p>
              </div>
              <Button
                icon={<RefreshCcw className="h-4 w-4" />}
                loading={tasksLoading}
                onClick={() => void loadFirstTaskPage(true)}
              >
                刷新
              </Button>
            </div>
            <Table<ComputerRuntimeLogRecall>
              rowKey="taskId"
              columns={columns}
              dataSource={tasks}
              loading={tasksLoading || detailLoading}
              pagination={false}
              size="small"
              scroll={{ x: 805 }}
              locale={{ emptyText: '暂无日志召回记录' }}
            />
            {nextPageToken ? (
              <div className="mt-4 text-center">
                <Button
                  loading={loadingMore}
                  onClick={() => void handleLoadMore()}
                >
                  加载更多
                </Button>
              </div>
            ) : null}
          </section>
        </div>
      ) : (
        <div className="py-12 text-center text-muted-foreground">
          {detailLoading ? '正在加载 Computer 详情…' : '未找到 Computer'}
        </div>
      )}
    </Drawer>
  );
}
